import { useLazyQuery, useQuery } from '@apollo/client';
import type { UsePackageFetch, UsePackageLazyFetch } from '@openx/types/package/PackageHookTypes';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes';
import { OPEN_AUDIENCE_ORG_NAME } from '@openx/utils/services/config';
import { useSearchParams } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context';
import { GetOpenxCuratedPackageDocument } from '../../../graphql/query/openxCuratedPackages/openxCuratedPackage.generated';
import { GetPackageDocument } from '../../../graphql/query/packages/GetPackage.generated';

export const usePackageFetch: UsePackageFetch = (packageId?: string | null) => {
  const [searchParams] = useSearchParams();
  const { session } = useAuth();
  const isOaSuborg = session?.account?.name !== OPEN_AUDIENCE_ORG_NAME;
  const isCurated = searchParams.get('is_curated') === 'true';
  const isCuratedFetch = isOaSuborg && isCurated;

  const { data, loading, refetch } = useQuery(GetPackageDocument, {
    skip: !packageId || isCuratedFetch,
    variables: { id: packageId as string },
  });

  const { data: curatedData, loading: curatedLoading } = useQuery(GetOpenxCuratedPackageDocument, {
    variables: { id: packageId as string },
    skip: !isCuratedFetch || !packageId,
  });

  if (isCuratedFetch) {
    const pckg = curatedData?.openxCuratedPackage?.obj ?? null;

    return {
      pckg,
      pckgLoading: curatedLoading,
      refetchPackage: refetch,
      remove: refetch,
    };
  }

  const packageById = data?.packageById;
  const pckg = packageById ? structuredClone(packageById.obj) : null;

  // Audience can be attached to package in non exported state which would not be reflected to targeting until audience
  // is "exported" Targeting update depending on package.audience_id field is required to show audience (independent of
  // status) on UI
  if (pckg && packageById?.audience_id) {
    pckg.targeting.audience = {
      openaudience_custom: {
        op: Intersect.INTERSECTS,
        val: `openaudience-${packageById.audience_id}`,
      },
    };
  }

  return {
    pckg,
    pckgLoading: loading,
    refetchPackage: refetch,
    remove: refetch,
  };
};

export const usePackageLazyFetch: UsePackageLazyFetch = (packageId?: string | null, onCompleted?) => {
  const [fn, { loading }] = useLazyQuery(GetPackageDocument, {
    onCompleted: data => {
      const packageById = data?.packageById;
      const pckg = packageById ? structuredClone(packageById.obj) : null;

      // Audience can be attached to package in non exported state which would not be reflected to targeting until audience
      // is "exported" Targeting update depending on package.audience_id field is required to show audience (independent of
      // status) on UI
      if (pckg && packageById?.audience_id) {
        pckg.targeting.audience = {
          openaudience_custom: {
            op: Intersect.INTERSECTS,
            val: `openaudience-${packageById.audience_id}`,
          },
        };
      }
      onCompleted?.(pckg || null);
    },
    variables: { id: packageId as string },
  });

  return {
    fetchPackage: fn,
    loading,
  };
};
